import { useCounterpartiesQueryData } from '@src/providers/Counterparties/useCounterpartiesQueryData';
import { prettifySubAccount, useSubAccounts } from '@src/providers/SubAccountsContext';
import type { OTCDealData } from '@src/types/OTC';
import { isFixedRateLoanDeal, isFloatingRateLoanDeal, isRepoDeal } from '@src/types/OTC/otcGuards';
import { useMarketAccountsContext } from '@talos/kyoko';
import { InspectorText } from '../InspectorText';
import { MarketAccountDisplay } from '../MarketAccountDisplay';
import type { DisplayOutputItem } from './types';

function dealSubAccountFieldName(deal: OTCDealData): string {
  if (isFixedRateLoanDeal(deal) || isFloatingRateLoanDeal(deal)) {
    return 'Loan Sub Account';
  }
  if (isRepoDeal(deal)) {
    return 'Repo Sub Account';
  }
  return 'Sub Account';
}

export const useCounterpartyInformation = (instrumentData: OTCDealData): DisplayOutputItem => {
  const { marketAccountsByName } = useMarketAccountsContext();
  const { subAccountsByName } = useSubAccounts();
  const { counterpartiesByName } = useCounterpartiesQueryData();

  const counterparty = instrumentData.Counterparty ? counterpartiesByName.get(instrumentData.Counterparty) : undefined;
  const counterpartyDisplay = counterparty?.DisplayName || counterparty?.Name || instrumentData.Counterparty;

  const subAccountDisplay = prettifySubAccount(instrumentData.SubAccount, subAccountsByName);
  const collateralSubAccountDisplay = prettifySubAccount(instrumentData.CollateralSubAccount, subAccountsByName);

  return {
    sectionTitle: 'Counterparty Information',
    fields: [
      {
        fieldName: 'Counterparty',
        fieldValue: <InspectorText>{counterpartyDisplay}</InspectorText>,
      },
      {
        fieldName: dealSubAccountFieldName(instrumentData),
        fieldValue: <InspectorText>{subAccountDisplay}</InspectorText>,
      },
      // Collateral sub-account only exists on secured deals, so omit the row entirely when absent.
      ...(collateralSubAccountDisplay
        ? [
            {
              fieldName: 'Collateral Sub Account',
              fieldValue: <InspectorText>{collateralSubAccountDisplay}</InspectorText>,
            },
          ]
        : []),
      {
        fieldName: 'Market Account',
        fieldValue: (
          <MarketAccountDisplay
            marketAccount={instrumentData.MarketAccount}
            marketAccountsByName={marketAccountsByName}
          />
        ),
      },
    ],
  };
};
